import { useEffect } from 'react';
import { Platform, StyleSheet, View, type ViewStyle } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
  Easing,
} from 'react-native-reanimated';

type BorderGlowProps = {
  children: React.ReactNode;
  style?: ViewStyle;
  color?: string;
  intensity?: 'low' | 'medium' | 'high';
};

export default function BorderGlow({
  children,
  style,
  color = '#3B82F6',
  intensity = 'medium',
}: BorderGlowProps) {
  const glowPulse = useSharedValue(0);
  const borderAlpha = useSharedValue(0.4);
  const mountScale = useSharedValue(0.97);
  const mountOpacity = useSharedValue(0);

  const maxShadow = intensity === 'low' ? 14 : intensity === 'medium' ? 28 : 44;

  const hex = color.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16) || 59;
  const g = parseInt(hex.slice(2, 4), 16) || 130;
  const b = parseInt(hex.slice(4, 6), 16) || 246;

  useEffect(() => {
    mountOpacity.value = withTiming(1, { duration: 400, easing: Easing.out(Easing.cubic) });
    mountScale.value = withTiming(1, { duration: 400, easing: Easing.out(Easing.back(1.2)) });

    glowPulse.value = withRepeat(
      withSequence(
        withTiming(1, { duration: 1600, easing: Easing.inOut(Easing.sin) }),
        withTiming(0, { duration: 1600, easing: Easing.inOut(Easing.sin) }),
      ),
      -1,
      false,
    );
    borderAlpha.value = withRepeat(
      withSequence(
        withTiming(0.9, { duration: 1200, easing: Easing.inOut(Easing.sin) }),
        withTiming(0.25, { duration: 1200, easing: Easing.inOut(Easing.sin) }),
      ),
      -1,
      false,
    );
  }, []);

  const containerStyle = useAnimatedStyle(() => ({
    opacity: mountOpacity.value,
    transform: [{ scale: mountScale.value }],
  }));

  const glowStyle = useAnimatedStyle(() => {
    const spread = 4 + glowPulse.value * maxShadow;
    const alpha = 0.12 + glowPulse.value * 0.38;
    if (Platform.OS !== 'web') {
      return {
        borderColor: `rgba(${r},${g},${b},${borderAlpha.value})`,
      };
    }
    return {
      borderColor: `rgba(${r},${g},${b},${borderAlpha.value})`,
      boxShadow: `0 0 ${spread}px rgba(${r},${g},${b},${alpha}), inset 0 0 ${spread * 0.4}px rgba(${r},${g},${b},${alpha * 0.3})`,
    } as any;
  });

  return (
    <Animated.View style={[containerStyle, { borderRadius: 18 }]}>
      <Animated.View style={[s.shell, glowStyle, style]}>
        <View style={s.inner}>{children}</View>
      </Animated.View>
    </Animated.View>
  );
}

const s = StyleSheet.create({
  shell: {
    borderRadius: 18,
    borderWidth: 1.2,
    padding: 1,
    backgroundColor: '#0d1117',
  },
  inner: { borderRadius: 17, overflow: 'hidden' },
});
